import DOMPurify, { Config } from 'dompurify';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ERROR_CONTENT_TYPE } from '@shared/enums/errors/ErrorContentTypes';

export type SanitizeProfile = 'page' | 'challenge';

const DOCUMENT_TAGS = ['html', 'head', 'body', 'title', 'meta'];

const COMMON_TAGS = [
  'style',
  'div',
  'span',
  'p',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img',
  'a',
  'b',
  'i',
  'em',
  'strong',
  's',
  'strike',
  'u',
  'big',
  'small',
  'sub',
  'sup',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'blockquote',
  'pre',
  'code',
];

const COMMON_ATTR = ['src', 'alt', 'title', 'class', 'style', 'lang', 'charset', 'width', 'height', 'href'];

const PAGE_TAGS = [...DOCUMENT_TAGS, ...COMMON_TAGS, 'iframe'];
const PAGE_ATTR = [...COMMON_ATTR, 'frameborder', 'allow', 'allowfullscreen', 'sandbox', 'loading', 'referrerpolicy'];

const CHALLENGE_TAGS = COMMON_TAGS;
const CHALLENGE_ATTR = COMMON_ATTR;

const ALLOWED_VARS = /\{\{\s*\.(Type|Code|Host|Error|Difficulty)\s*\}\}/g;

const IGNORED_NODES = new Set(['#comment', 'body', '#text']);

const PAGE_CONFIG: Config = {
  WHOLE_DOCUMENT: true,
  ALLOW_DATA_ATTR: false,
  ALLOWED_TAGS: PAGE_TAGS,
  ALLOWED_ATTR: PAGE_ATTR,
};

const CHALLENGE_CONFIG: Config = {
  ALLOW_DATA_ATTR: false,
  ALLOWED_TAGS: CHALLENGE_TAGS,
  ALLOWED_ATTR: CHALLENGE_ATTR,
};

export function sanitizeHtmlFragment(fragment: string): string {
  return DOMPurify.sanitize(fragment ?? '', CHALLENGE_CONFIG);
}

export function safeHtmlValidator(profile: SanitizeProfile): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const html: string = control.value;
    if (!html) {
      return null;
    }

    const contentType = control.parent?.get('contentType');
    if (contentType && contentType.value !== ERROR_CONTENT_TYPE.HTML) {
      return null;
    }

    const violations = collectViolations(html, profile === 'challenge' ? CHALLENGE_CONFIG : PAGE_CONFIG);
    return violations.length ? { unsafeHtml: violations } : null;
  };
}

function collectViolations(html: string, config: Config): string[] {
  DOMPurify.sanitize(html, config);

  const violations = new Set<string>();
  for (const entry of DOMPurify.removed) {
    if ('element' in entry && entry.element) {
      const tag = entry.element.nodeName.toLowerCase();
      if (IGNORED_NODES.has(tag)) {
        continue;
      }
      violations.add(`<${tag}>`);
    } else if ('attribute' in entry && entry.attribute) {
      violations.add(`${entry.attribute.name} (<${entry.from.nodeName.toLowerCase()}>)`);
    }
  }

  if (html.replace(ALLOWED_VARS, '').includes('{{')) {
    violations.add('{{ }}');
  }
  return [...violations];
}
